import { animated, SpringConfig, useSpring, useSpringRef } from '@react-spring/web';
import classNames from 'classnames';
import React, { LegacyRef, useEffect, useRef } from 'react';
import { useMeasure } from 'react-use';
import { useNeueLayoutMode } from '../helpers/neue-layout-mode.hook';
import { Section } from '../types';
import { ScrollDirection, useScrollDirection } from '../utils/scroll-direction.hook';
import { NeuePlayerDesktopContent } from './desktop-content';
import { DesktopNavigationBar } from './desktop-navigation-bar';
import { MobilePortraitNavigationBar } from './mobile-portrait-navigation-bar';
import { MobilePortraitNavigationBarAlt } from './mobile-portrait-navigation-bar-alt';
import { usePlayerStore } from './player-store';

const LOGO_ANIMATION_CONFIG: SpringConfig = {
  mass: 1,
  tension: 400,
  friction: 40,
};

export const NeuePlayerMobileView = () => {
  const {
    layout: { renderElements, innerActualHeight, innerActualWidth, layoutReady },
    sectionDrawerOpen,
    canvasScrollPosition,
    setLayoutSizes,
    setBlockContentSize,
    setCanvasScrollPosition,
    setPlayerContentElement,
  } = usePlayerStore();
  const { layoutMode } = useNeueLayoutMode();
  const [innerLegacyRef, innerRect] = useMeasure();
  const innerRef = useRef<HTMLDivElement>();
  const [editorContentLegacyRef, editorContentRect] = useMeasure();
  const [editorContentEl, setEditorContentEl] = React.useState<HTMLDivElement | null>(null);

  const [lastScrollDirection, setLastScrollDirection] = React.useState<ScrollDirection>('up');
  const showLogo = sectionDrawerOpen || lastScrollDirection === 'up' || canvasScrollPosition.y === 0;
  const logoStyles = useSpring({
    y: showLogo ? 0 : -100,
    config: LOGO_ANIMATION_CONFIG,
  });

  useScrollDirection(editorContentEl, setLastScrollDirection);

  useEffect(() => {
    if (editorContentEl) {
      const handleScroll = () => {
        setCanvasScrollPosition(editorContentEl.scrollLeft, editorContentEl.scrollTop);
      };
      editorContentEl.addEventListener('scroll', handleScroll);
      return () => {
        editorContentEl.removeEventListener('scroll', handleScroll);
      };
    }
  }, [editorContentEl]);

  useEffect(() => {
    if (innerRef.current && editorContentEl) {
      const innerRect = innerRef.current.getBoundingClientRect();
      const editorContentRect = editorContentEl.getBoundingClientRect();
      setLayoutSizes({
        width: innerRect.width,
        height: editorContentRect.height, // Note, need this to be the height of the editorContent, not the outer area
      });
      setPlayerContentElement(editorContentEl);
    }
  }, [innerRect, editorContentRect]);

  return (
    <div className='w-full h-full bg-white overflow-hidden relative'>
      <animated.div
        className='absolute z-[300] pt-2 h-[50px] left-0 w-full px-4 flex items-center justify-start'
        style={{
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0) 100%)',
          backgroundSize: '100% 100%',
          transform: logoStyles.y.to((y) => `translateY(${y}%)`),
        }}></animated.div>
      <div
        className='w-full h-full overflow-y-auto scrollbar-hidden flex flex-col px-4'
        ref={
          ((element: HTMLDivElement) => {
            setEditorContentEl(element);
            editorContentLegacyRef(element);
          }) as LegacyRef<HTMLDivElement>
        }
      >
        <div
          className={classNames('relative flex-1', {})}
          ref={
            ((element: HTMLDivElement) => {
              innerRef.current = element;
              innerLegacyRef(element);
            }) as LegacyRef<HTMLDivElement>
          }
        >
          {layoutReady && (
            <NeuePlayerDesktopContent
              width={innerActualWidth || 0}
              height={innerActualHeight || 0}
              renderElements={renderElements}
              onBlockSize={(blockId, size) => setBlockContentSize(blockId, size)}
            />
          )}
        </div>
      </div>
      {(layoutMode === 'mobile-portrait' || layoutMode === 'mobile-landscape') && (
        <div className='absolute z-[300] inset-0 pointer-events-none'>
          {/* <MobilePortraitNavigationBar /> */}
          <MobilePortraitNavigationBarAlt />
        </div>
      )}
    </div>
  );
};
